import * as S from './styles';
import { useSync } from '@/hooks';
import { Modal } from '@/components';
import { useMemo, useRef } from 'react';

const GlobalModalSync = () => {
  const lastSpeedBps = useRef(0);

  const {
    isLoading,
    hasError,
    noUpdate,
    isSuccess,
    preparing,
    isOpenedModal,
    downloadSpeedBps,
    fileDownload,
    downloadingFiles,
    downloadingFileName,
    fileDownloadProgress,
    progressPercent,
    isDownloadingFiles,
    setIsOpenedModal,
  } = useSync();

  const currentLoading = isLoading || preparing;

  const speedLabel = useMemo(() => {
    const value = downloadSpeedBps || lastSpeedBps.current;
    if (downloadSpeedBps !== 0) lastSpeedBps.current = downloadSpeedBps;
    const kbps = (value * 8) / 1000;
    if (kbps >= 1000) {
      const mbps = (kbps / 1000).toFixed(1).replace(/\.0$/, '');
      return `${mbps}Mbps`;
    }
    return `${Math.round(kbps)}kbps`;
  }, [downloadSpeedBps]);

  const errorOnSync = useMemo(() => {
    return hasError && !currentLoading;
  }, [hasError, currentLoading]);

  const syncMessage = useMemo(() => {
    if (errorOnSync) return 'Falha na sincronização dos dados, por favor tente novamente!'
    if (noUpdate) return 'Não há atualizações disponíveis.'
    if (isSuccess) return 'Sua sincronização já está concluída. Você já pode utilizar o app normalmente.'
    return 'Estamos sincronizando seu aplicativo, logo você poderá navegar normalmente.';
  }, [errorOnSync, noUpdate, isSuccess]);

  const syncTitle = useMemo(() => {
    if (errorOnSync) return 'Falha na Sincronização'
    if (isSuccess) return 'Concluído'
    if (noUpdate) return 'Tudo certo.'
    return 'Aguarde...';
  }, [errorOnSync, isSuccess, noUpdate]);

  const syncQtdMessage = useMemo(() => {
    return `${Math.min(isDownloadingFiles.length, downloadingFiles.length)}/${downloadingFiles.length}`;
  }, [isDownloadingFiles, downloadingFiles]);

  const closeModal = () => {
    if (noUpdate) {
      setIsOpenedModal(false);
      return;
    }
    if (!isOpenedModal) return;

    const reload = async () => {
      window.location.reload();
    }
    reload();
  }

  return (
    <Modal id="modal-sync" isOpen={isOpenedModal} isLoading={true} closeModal={closeModal}>
      <S.Container>
        {currentLoading && !isSuccess && (
          <S.BigLoadContent>
            <S.BigLoad>
              <S.CentralLoad />
            </S.BigLoad>
          </S.BigLoadContent>
        )}

        {errorOnSync && <S.Error size={76} />}
        {isSuccess && <S.Success size={76} />}

        <S.LoadingMsg>{syncTitle}</S.LoadingMsg>

        <S.Loader>
          <S.Message>{syncMessage}</S.Message>
        </S.Loader>

        {!noUpdate && (
          <S.ProgressContent $collapsed={false}>
            <S.ProgressBar $progress={progressPercent}>
              <S.ProgressStyle id="progress-bar" style={{ width: `${progressPercent}%` }} />
              <S.ProgressText>
                <S.ProgressTextLayer $overProgress={false}>
                  <S.Span>{fileDownloadProgress}</S.Span>
                  <S.Span>{fileDownload}</S.Span>
                </S.ProgressTextLayer>
                <S.ProgressTextLayer $overProgress={true}>
                  <S.Span>{fileDownloadProgress}</S.Span>
                  <S.Span>{fileDownload}</S.Span>
                </S.ProgressTextLayer>
              </S.ProgressText>
            </S.ProgressBar>
          </S.ProgressContent>
        )}

        {currentLoading && !isSuccess && !!downloadSpeedBps && isDownloadingFiles.length > 0 && (
          <>
            <S.Row>
              <S.InternetSpeed>{speedLabel}</S.InternetSpeed>
              <S.FileLoading>Sincronizando {downloadingFileName}</S.FileLoading>
              <S.FilesNumber>{syncQtdMessage}</S.FilesNumber>
            </S.Row>

            <S.LittleLoading>
              <S.LittleLoadingPulse />
            </S.LittleLoading>
          </>
        )}

        {isSuccess && <S.SuccessText>Sincronização concluída!</S.SuccessText>}

        {(isSuccess || errorOnSync || noUpdate) && (
          <S.ButtonClose data-test="modal-sync-close-button" onClick={closeModal}>
            Fechar
          </S.ButtonClose>
        )}
      </S.Container>
    </Modal>
  );
};

export default GlobalModalSync;